// src/pages/LostPetTag/LostPetTag.jsx
import React from "react";
import { Container, Row, Col, Button, Card } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import "./LostPetTag.css";

export default function LostPetTag() {
  const navigate = useNavigate();

  const goBuy = () => {
    navigate("/");
    requestAnimationFrame(() => {
      const scroller =
        document.querySelector("#root") ||
        document.querySelector(".App") ||
        document.querySelector(".app-shell") ||
        document.querySelector(".app-content");

      if (scroller) scroller.scrollTo({ top: 0, left: 0, behavior: "auto" });

      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    });
  };

  return (
    <div className="lost-pet-wrapper">
      {/* HERO (SELF-PROJECTION) */}
      <section className="lost-hero">
        <Container>
          <Row className="justify-content-center">
            <Col lg={9} xl={8} className="text-center">
              <div className="lost-kicker">LOST PET PROTECTION</div>

              <h1 className="lost-title">
                If your pet got lost today… would a stranger know how to reach you?
              </h1>

              <p className="lost-subtitle">
                Most tags rely on a public phone number. If it’s outdated, unreadable,
                or you don’t want it exposed — help gets delayed.
              </p>

              <p className="lost-subtitle">
                SmartTag gives your pet a simple, clear ID — a unique{" "}
                <strong>LP ID</strong> — and a safer way for a finder to contact you
                through Little Paws Place.
              </p>

              <div className="lost-hero-cta">
                <Button className="lost-primary-cta" size="lg" onClick={goBuy}>
                  Protect My Pet for $23.99
                </Button>

                <Button
                  variant="outline-light"
                  size="lg"
                  className="lost-secondary-cta"
                  onClick={() => navigate("/plans")}
                >
                  View plans
                </Button>
              </div>

              <div className="lost-hero-trust">
                No login required to buy • Activate after delivery • Privacy-first contact • No GPS subscription fees
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* REAL-WORLD SCENARIO (MENTAL MOVIE) */}
      <section className="lost-section">
        <Container>
          <Row className="justify-content-center">
            <Col lg={10} xl={9}>
              <h2 className="lost-h2 text-center mb-3">
                What actually happens when a pet is found
              </h2>
              <p className="lost-muted text-center mb-4">
                In real life, people want to help — but they need one clear next step.
              </p>

              <Row className="g-3">
                <Col md={4}>
                  <Card className="lost-step-card h-100">
                    <Card.Body>
                      <div className="lost-step-number">1</div>
                      <h3 className="lost-h3">A stranger finds your pet</h3>
                      <p className="lost-body">
                        They don’t know you. They don’t know what vet you use. They
                        just want to do the right thing quickly.
                      </p>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={4}>
                  <Card className="lost-step-card h-100">
                    <Card.Body>
                      <div className="lost-step-number">2</div>
                      <h3 className="lost-h3">They use the LP ID</h3>
                      <p className="lost-body">
                        The tag shows a simple ID (example: <strong>LP1001</strong>) so
                        there’s no guessing and no “what do I do now?”
                      </p>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={4}>
                  <Card className="lost-step-card h-100">
                    <Card.Body>
                      <div className="lost-step-number">3</div>
                      <h3 className="lost-h3">You get contacted safely</h3>
                      <p className="lost-body">
                        They can reach you through your SmartTag profile — without your
                        personal phone number being displayed publicly.
                      </p>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <div className="lost-inline-cta mt-4 text-center">
                <Button className="lost-primary-cta" size="lg" onClick={goBuy}>
                  Get a SmartTag
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* HOW IT WORKS (SIMPLIFIED) */}
      <section className="lost-section lost-alt">
        <Container>
          <Row className="justify-content-center">
            <Col lg={10} xl={9}>
              <h2 className="lost-h2 text-center mb-3">How SmartTag Works</h2>
              <p className="lost-muted text-center mb-4">
                Built for the exact “found pet” moment — not just a cute accessory.
              </p>

              <Row className="g-3">
                <Col md={4}>
                  <Card className="lost-step-card h-100">
                    <Card.Body>
                      <div className="lost-step-number">1</div>
                      <h3 className="lost-h3">Attach it</h3>
                      <p className="lost-body">
                        Your pet wears SmartTag on their collar with a visible{" "}
                        <strong>LP ID</strong>.
                      </p>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={4}>
                  <Card className="lost-step-card h-100">
                    <Card.Body>
                      <div className="lost-step-number">2</div>
                      <h3 className="lost-h3">Activate after delivery</h3>
                      <p className="lost-body">
                        When it arrives, you connect that LP ID to your pet profile in
                        Little Paws Place.
                      </p>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={4}>
                  <Card className="lost-step-card h-100">
                    <Card.Body>
                      <div className="lost-step-number">3</div>
                      <h3 className="lost-h3">Optional nearby help</h3>
                      <p className="lost-body">
                        Bluetooth can help in “nearby but hidden” situations (like a
                        backyard, woods, or under a porch).
                      </p>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <div className="lost-note mt-3 text-center">
                <span className="lost-note-pill">
                  Designed for identification + nearby recovery support • No GPS subscription fees
                </span>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* WHY TRADITIONAL TAGS FAIL (PAIN) */}
      <section className="lost-section">
        <Container>
          <Row className="justify-content-center">
            <Col lg={9} xl={8}>
              <h2 className="lost-h2">Why traditional tags fall short</h2>

              <p className="lost-body">
                Traditional tags usually show a name and a phone number. That sounds
                simple — until the number changes, the tag gets scratched, or you don’t
                want your personal number publicly visible.
              </p>

              <p className="lost-body">
                When the finder isn’t sure what to do, they may delay, move on, or post
                online hoping you’ll see it.
              </p>

              <p className="lost-body">
                SmartTag is designed to remove friction in that moment: a clear ID and
                a safe path to contact.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* WHY SMARTTAG (BENEFIT-FIRST) */}
      <section className="lost-section lost-alt">
        <Container>
          <Row className="justify-content-center">
            <Col lg={9} xl={8}>
              <h2 className="lost-h2">Why SmartTag works better</h2>

              <ul className="lost-list">
                <li>
                  <strong>Clear “what to do next”:</strong> LP ID is easy for a finder to use
                </li>
                <li>
                  <strong>Privacy-first contact:</strong> get reached without exposing your personal number
                </li>
                <li>
                  <strong>Less dependency on memory:</strong> not “call this number if it’s still correct”
                </li>
                <li>
                  <strong>Built for real recovery:</strong> identification + optional nearby Bluetooth support
                </li>
                <li>
                  <strong>Works even if you’re busy:</strong> a structured flow beats “hope you see a post”
                </li>
              </ul>

            {/* STICKY MOBILE CTA */}
<div className="lost-sticky-cta">
  <div className="lost-sticky-cta-inner">
    <div className="lost-sticky-cta-text">
      Protect My Pet
      <small>$23.99 • No subscription fees</small>
    </div>

    <Button className="lost-primary-cta" onClick={goBuy}>
      Buy
    </Button>
  </div>
</div>

            </Col>
          </Row>
        </Container>
      </section>

      {/* BONUS VALUE */}
      <section className="lost-section">
        <Container>
          <Row className="justify-content-center">
            <Col lg={9} xl={8}>
              <h2 className="lost-h2">More than a tag</h2>
              <p className="lost-body">
                SmartTag connects your pet to Little Paws Place — so you’re not only
                protected in emergencies, you can also stay organized day-to-day.
              </p>

              <ul className="lost-list">
                <li>Pet profile with photos &amp; key details</li>
                <li>AI-powered feeding plans (calories, cups/day, reminders)</li>
                <li>Notes, health reminders, and document storage</li>
                <li>Ongoing tools built for busy pet parents</li>
              </ul>
            </Col>
          </Row>
        </Container>
      </section>

      {/* FINAL CTA (ANCHORS) */}
      <section className="lost-final">
        <Container>
          <Row className="justify-content-center">
            <Col lg={9} xl={8} className="text-center">
              <h2 className="lost-h2 mb-2">Give your pet a safer way home</h2>

              <p className="lost-muted mb-4">
                One-time purchase. Private contact. Built for real “found pet” moments.
              </p>

              <div className="lost-final-price">
                <div className="lost-price-line">SmartTag</div>
                <div className="lost-price">$23.99</div>
                <div className="lost-muted small">
                  No login required • Activate after delivery • No GPS subscription fees
                </div>
              </div>

              <div className="lost-final-cta">
                <Button className="lost-primary-cta" size="lg" onClick={goBuy}>
                  Buy a SmartTag
                </Button>
              </div>

              <div className="lost-hero-trust mt-3">
                Privacy-first contact • LP ID identification • Optional nearby Bluetooth support
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* FAQ (LIGHTLY REWRITTEN) */}
      <section className="lost-section">
        <Container>
          <Row className="justify-content-center">
            <Col lg={9} xl={8}>
              <h2 className="lost-h2">Frequently Asked Questions</h2>

              <div className="lost-faq">
                <div className="lost-faq-item">
                  <div className="lost-faq-q">Do I need to log in to buy a SmartTag?</div>
                  <div className="lost-faq-a">
                    No. Purchasing is public. You activate after delivery using your
                    LP ID.
                  </div>
                </div>

                <div className="lost-faq-item">
                  <div className="lost-faq-q">How does someone use it if they find my pet?</div>
                  <div className="lost-faq-a">
                    The tag shows an LP ID (example: LP1001). A finder uses that ID to
                    follow your SmartTag contact flow through Little Paws Place.
                  </div>
                </div>

                <div className="lost-faq-item">
                  <div className="lost-faq-q">Is this GPS tracking?</div>
                  <div className="lost-faq-a">
                    SmartTag is not GPS tracking. It’s designed for identification and
                    nearby recovery support, with optional Bluetooth help when you’re
                    close.
                  </div>
                </div>

                <div className="lost-faq-item">
                  <div className="lost-faq-q">Is my phone number visible?</div>
                  <div className="lost-faq-a">
                    No. SmartTag helps a finder reach you without publicly displaying
                    your personal phone number.
                  </div>
                </div>

                <div className="lost-faq-item">
                  <div className="lost-faq-q">Can I use one tag for multiple pets?</div>
                  <div className="lost-faq-a">
                    Each pet should have their own SmartTag so their LP ID and profile
                    stay accurate.
                  </div>
                </div>
              </div>

              <div className="text-center mt-4">
                <Button
                  variant="outline-dark"
                  className="lost-secondary-dark"
                  onClick={() => navigate("/")}
                >
                  Back to Home
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
}
